import React, { useEffect, useState } from 'react';
import { TextField, Button, Select, MenuItem, InputLabel, FormControl, SelectChangeEvent, Paper, Typography, IconButton, Box, useTheme, Grid2, Switch } from '@mui/material';
import { Cancel, HelpOutline, KeyboardArrowDown, KeyboardArrowUp, KeyboardDoubleArrowDown, KeyboardDoubleArrowUp, North, South } from '@mui/icons-material';
import ImageUpload from '../CommonComponents/ImageUpload';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Actions, RootState } from '../../Redux/Reducer/rootReducer';
import { APICallStatus, Priority, ServiceRequestStatus, ServiceRequestTypes, Unit, UserType } from '../../Types/UnitFeatureTypes';
import DropdownSelection from '../CommonComponents/DropdownSelection';
import Editor from '../TextEditor/Editor';
import { useAuth } from '../../context/authContext';
import { createServiceRequestAPIStatus, currentUnit, fetchUnit } from '../../Redux/selectors';
import { useApp } from '../../context/appContext';

type ServiceRequestFormProps = {
  onSubmit: (data: ServiceRequestData) => void;
};

interface ServiceRequestData {
  title: string;
  description: string;
  priority: Priority;
  status: ServiceRequestStatus;
  type: ServiceRequestTypes;
  tempImages: string[];
  has_permission_to_enter: boolean;
  unit: number;
  requester: number;
}

const ServiceRequestForm: React.FC<ServiceRequestFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<ServiceRequestData>({
    title: '',
    description: '',
    priority: Priority.LOW,
    status: ServiceRequestStatus.PENDING,
    type: ServiceRequestTypes.GENERAL,
    has_permission_to_enter: false,
    tempImages: [],
    unit: -1,
    requester: -1
  });
  const theme = useTheme();
  interface ServiceRequestFormErrors {
    title?: string;
    description?: string;
    unit?: string;
    occupant?: string;
    priority?: string;
  }
  
  const [errors, setErrors] = useState<ServiceRequestFormErrors>({});
  const dispatch = useDispatch();
  const {user} = useAuth();
  const unitList = useSelector(fetchUnit);
  const IS_MANAGER = user?.type === UserType.MANAGER;
  const currentUnitData = useSelector(currentUnit) as Unit;
  const [selectedUnit, setSelectedUnit] = useState<Unit>();
  const [selectedUnitOccupant, setSelectedUnitOccupant] = useState<number | null>(null);
  const createServiceAPIStatus = useSelector(createServiceRequestAPIStatus);
  const createdServiceRequestId = useSelector((state: RootState) => state.createServiceRequest.request.serviceRequestId);
  const { setSnackbar } = useApp();


  useEffect(() => {
    if (createServiceAPIStatus === APICallStatus.SUCCESS) {
      setSnackbar(true, 'Service Request Created Successfully', 'success');
      if (IS_MANAGER) {
        dispatch(Actions.serviceRequestUpdates.addServiceRequestUpdateAction.request({
          serviceRequestUpdateData: {
            resource_id: createdServiceRequestId ?? -1,
            comment: 'Service Request Created By Manager',
            commenter_id: -1,
            user: {
              first_name: user.first_name ?? '',
              last_name: user.last_name ?? '',
              avatar: user.avatar ?? '',
              id: user.id ?? -1
          }
        }
        }));
      }
      navigate(-1);
    } else if (createServiceAPIStatus === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to create Service Request', 'error');
    }
    dispatch(Actions.serviceRequest.resetStatusRequestAction());
  }, [createServiceAPIStatus]);

  useEffect(() => {
    if (selectedUnit) {
    setSelectedUnit(unitList.find((unit) => unit.id === selectedUnit?.id));
    }
  },[unitList]);

  const navigate = useNavigate();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<any>, field: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: event.target.value
    }));
  };

  const validateForm = () => {
    const newErrors: Partial<ServiceRequestFormErrors> = {};
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (IS_MANAGER && !selectedUnit) newErrors.unit = 'Unit is required';
    if (IS_MANAGER && !selectedUnitOccupant) newErrors.occupant = 'Requesting User is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

    const handleUnitChange = (e: SelectChangeEvent<string>) => {
      let unitDetails = unitList.find((unit) => parseInt(e.target.value) === unit.id);
      if (!unitDetails?.owner && unitDetails?.id ) {
        dispatch(Actions.fetchUnit.unitOccupantsActions.request({ id: unitDetails.id }));
      }
      setSelectedUnit(unitDetails);
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // setFormData((prevState) => ({
    //   ...prevState,
    //   description: document.querySelector('.ql-editor')?.innerHTML || '',
    // }));

    if (validateForm()) {
      // onSubmit(formData);

      dispatch(Actions.serviceRequest.addServiceRequestAction.request({
        serviceRequestData: {
          title: formData.title,
          description: formData.description,
          serviceType: formData.type,
          priority: formData.priority,
          status: ServiceRequestStatus.NEW,
          has_permission_to_enter: formData.has_permission_to_enter,
          reporter_id: IS_MANAGER ? (selectedUnitOccupant ?? -1) : user?.id,
          reporter_unit_id: IS_MANAGER ? (selectedUnit?.id ?? -1) : (currentUnitData?.id ?? -1),
        }
      }));
      setErrors({});
    }
  };

  const handleImageChange = (files: File[]) => {
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setFormData((prevState) => ({
      ...prevState,
      tempImages: [...prevState.tempImages, ...imageUrls],
    }));
  };

  const handleRemoveImage = (index: number) => {
      setFormData((prevState) => ({
        ...prevState,
        tempImages: prevState.tempImages.filter((_, i) => i !== index),
      }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      has_permission_to_enter: e.target.checked,
    }));
  };

  return (
    <Paper elevation={5} sx={{ padding: 6, borderRadius: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h5" gutterBottom>
        Service Request
      </Typography>
      <IconButton 
          sx={{ '&:hover': {
            color: 'error.main',
          } }}
          onClick={() => navigate(-1)}>
          <Cancel />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Title"
          variant="outlined"
          name="title"
          placeholder='Please provide a title for the issue.'
          margin='normal'
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title}
        />
        {/* <RichTextEditor
          value={formData.description}
          onChange={(value) => setFormData((prevState) => ({
            ...prevState,
            description: value,
          }))}
          error={!!errors.description}
          helperText={errors.description}
        /> */}
        <Editor
          value={formData.description}
          isReadOnly={false}
          placeholder='Please write a description here.'
          error={!!errors.description}
          helperText={errors.description}
          onChange={(value) => setFormData((prevState) => ({
            ...prevState,
            description: value,
          }))}
        />
        <FormControl fullWidth error={!!errors.priority}>
          <Grid2 container spacing={2}>
            <Grid2 size={{xs: 12, md: 6}}>
          <DropdownSelection
            dropDownLabel='Priority'
            showHeader={false}
            labelSx={{mt:1.5}}
            currentValue={formData.priority}
            selectionValues={[
              { value: Priority.LOWEST, label: 'Lowest', color: 'grey', icon: KeyboardDoubleArrowDown },
              { value: Priority.LOW, label: 'Low', color: 'blue', icon: KeyboardArrowDown },
              { value: Priority.MEDIUM, label: 'Medium', color: theme.palette.warning.main, icon: KeyboardArrowUp },
              { value: Priority.HIGH, label: 'High', color: '#953271', icon: KeyboardArrowUp },
              { value: Priority.CRITICAL, label: 'Highest', color: theme.palette.error.main, icon: KeyboardDoubleArrowUp },
            ]}
            variant='outlined'
            sxObject={{ mb: 2, mt: 1.5}}
            handleOnChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => handleSelectChange(event, 'priority')}
          /></Grid2>
          <Grid2 size={{xs: 12, md: 6}}>
          <DropdownSelection
                dropDownLabel="Type"
                showHeader={false}
                labelSx={{mt:1.5}}
                currentValue={formData.type}
                sxObject={{ mb: 2, mt: 1.5}}
                variant='outlined'
                selectionValues={[
                  { value: ServiceRequestTypes.GENERAL, label: 'General', color: 'default' },
                  { value: ServiceRequestTypes.COMPLAINTS, label: 'Complaints', color: 'default' },
                  { value: ServiceRequestTypes.FEEDBACK, label: 'Feedback', color: 'default' },
                  { value: ServiceRequestTypes.FOB_REQUEST, label: 'Fob Request', color: 'default' },
                  { value: ServiceRequestTypes.MAINTENANCE, label: 'Maintenance', color: 'default' },
                  { value: ServiceRequestTypes.PARKING_ISSUE, label: 'Parking Issue', color: 'default' },
                  { value: ServiceRequestTypes.TENANT_RELATED, label: 'Tenant Related', color: 'default' },
                  { value: ServiceRequestTypes.EMERGENCY, label: 'Emergency', color: 'default' },
                  { value: ServiceRequestTypes.OTHER, label: 'Other', color: 'default' },
                ]}
                handleOnChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => handleSelectChange(event, 'type')}
              /></Grid2>
              {IS_MANAGER && <><Grid2 size={{xs: 12, md: 6}}>
              <DropdownSelection
                dropDownLabel='Select Unit'
                showHeader={false}
                currentValue={selectedUnit?.id?.toString() ?? ''}
                selectionValues={unitList.map((unit) => ({ value: unit.id.toString(), label: unit.unit_number.toString(), color: 'default' }))}
                variant='outlined'
                handleOnChange={handleUnitChange}
              />
              </Grid2>
              <Grid2 sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '2px solid #555', p: 1 }} size={{xs: 12, md: 6}}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HelpOutline />
                  <Typography variant="subtitle1" color="textSecondary">
                    Permission to access the unit
                  </Typography>
                </Box>
                <Switch checked={formData.has_permission_to_enter} onChange={handleSwitchChange} />
              </Grid2>
              {selectedUnit && <Grid2 size={{xs: 12, md: 6}}>
              <DropdownSelection
                dropDownLabel='Select Occupant'
                showHeader={false}
                currentValue={selectedUnitOccupant?.toString() ?? ''}
                selectionValues={
                  [
                    ...(selectedUnit?.owner ? [{ value: selectedUnit.owner.id.toString(), label: `${selectedUnit.owner.name} (Owner)`, color: 'green' }] : []),
                    ...(selectedUnit?.tenants?.map((tenant) => ({ value: tenant.id.toString(), label: tenant.name, color: 'default' })) ?? [])
                  ]}
                variant='outlined'
                handleOnChange={(e) => setSelectedUnitOccupant(parseInt(e.target.value))}
              />
              </Grid2>}
              </>}  
            </Grid2>
          <ImageUpload
              imagePreviews={formData.tempImages}
              handleImageChange={handleImageChange}
              handleRemoveImage={handleRemoveImage}
              reviewUpload={true}
          />
          </FormControl>
        
        <Button sx={{mt: 2}} variant="contained" color="primary" fullWidth type="submit">Submit Request</Button>
      </form>
    </Paper>
  );
};

export default ServiceRequestForm;
